/**
 * /upstream — bat/tat dich vu tra phi lam duong lui.
 *
 * ============================================================================
 *  TRANG NAY LA CHO DE PHAN BOI TINH CHAT 2 NHAT ("doc lap").
 *
 *  `capforge` phai co gia tri ngay ca khi khong cau hinh dich vu tra phi nao. Upstream
 *  la PLUG-IN, mac dinh TAT. Vi vay trang nay khong duoc thiet ke nhu mot buoc cai dat
 *  bat buoc — no phai noi ro rang de nguyen trang thai tat la mot lua chon dung.
 *
 *  Va mot dieu ky thuat: API key cua provider KHONG BAO GIO duoc doc nguoc ve. Server
 *  chi tra `apiKeyMasked` (vd "••••ab12"). O truong nhap, de trong = giu nguyen key cu;
 *  chi khi go gia tri moi thi key moi bi thay. Hien key that ra man hinh la mot lo hong,
 *  va "tien cho nguoi dung" khong doi duoc dieu do.
 * ============================================================================
 */

import { useEffect, useState } from 'react';
import type { UpstreamSettingsDto } from '@capforge/shared';
import { api, errorMessage } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge } from '../components/Badge';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';

export default function Upstream(): JSX.Element {
  const state = useAsync<UpstreamSettingsDto>((signal) => api.upstream(signal), []);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState('2captcha');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [timeoutMs, setTimeoutMs] = useState(180_000);
  const [softId, setSoftId] = useState(0);
  const [onlyProfiles, setOnlyProfiles] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dto = state.data;

  useEffect(() => {
    if (dto === null) return;
    setEnabled(dto.enabled);
    setProvider(dto.provider);
    setBaseUrl(dto.baseUrl);
    setTimeoutMs(dto.timeoutMs);
    setSoftId(dto.softId);
    setOnlyProfiles(dto.onlyProfiles.join(', '));
    setApiKey(''); // KHONG bao gio dien san key — server khong tra ve key that.
  }, [dto]);

  async function save(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const body: Record<string, unknown> = {
        enabled,
        provider,
        baseUrl,
        timeoutMs,
        softId,
        onlyProfiles: onlyProfiles
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      };
      // De trong = giu key cu. Gui chuoi rong se XOA key dang co — mot cach mat cau hinh
      // rat de xay ra neu nguoi dung chi muon doi timeout.
      if (apiKey.trim().length > 0) body['apiKey'] = apiKey.trim();

      await api.updateUpstream(body);
      setApiKey('');
      setSaved(true);
      state.reload();
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (state.loading) return <Spinner />;
  if (state.error !== null) return <ErrorBox message={state.error} onRetry={state.reload} />;
  if (dto === null) return <ErrorBox message="Không đọc được cấu hình upstream" onRetry={state.reload} />;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold">Upstream (dịch vụ trả phí)</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Đường lui khi solver nội bộ không giải được. <strong>Mặc định TẮT</strong>, và để nguyên
          như vậy là một lựa chọn đúng: <code className="cf-mono">capforge</code> được thiết kế để
          có giá trị ngay cả khi không cấu hình dịch vụ trả phí nào.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <Badge tone={dto.enabled ? 'success' : 'neutral'}>{dto.enabled ? 'ĐANG BẬT' : 'ĐANG TẮT'}</Badge>
        <Badge tone={dto.configured ? 'info' : 'warn'}>
          {dto.configured ? `Đã có key ${dto.apiKeyMasked ?? ''}` : 'Chưa có API key'}
        </Badge>
        <Badge tone="neutral">
          Nguồn cấu hình: {dto.source === 'db' ? 'dashboard' : 'biến môi trường'}
        </Badge>
        {dto.enabled && !dto.configured && (
          <span className="text-sm text-amber-700 dark:text-amber-400">
            Đang bật nhưng chưa có API key — mọi request sẽ lặng lẽ không có đường lui.
          </span>
        )}
      </div>

      <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="text-sm font-medium">Bật đường lui upstream</span>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="cf-label" htmlFor="up-provider">
              Provider
            </label>
            <select
              id="up-provider"
              className="cf-input"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {/* Neu cau hinh dang luu mot ten khong co trong registry thi van phai hien
                  no ra, neu khong nguoi dung se khong hieu vi sao `configured` la false. */}
              {!dto.availableProviders.includes(provider) && (
                <option value={provider}>{provider} (không có trong registry)</option>
              )}
              {dto.availableProviders.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="cf-label" htmlFor="up-key">
              API key của provider
            </label>
            <input
              id="up-key"
              type="password"
              className="cf-input"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={dto.configured ? `${dto.apiKeyMasked ?? '••••'} — để trống = giữ nguyên` : 'chưa có'}
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Server chỉ lưu và trả về bản đã che. Để trống ở đây thì key hiện tại được giữ nguyên.
            </p>
          </div>

          <div>
            <label className="cf-label" htmlFor="up-base">
              baseUrl
            </label>
            <input
              id="up-base"
              className="cf-input cf-mono"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>

          <div>
            <label className="cf-label" htmlFor="up-timeout">
              timeoutMs
            </label>
            <input
              id="up-timeout"
              type="number"
              className="cf-input"
              value={timeoutMs}
              min={1000}
              max={600_000}
              onChange={(e) => setTimeoutMs(Number(e.target.value))}
            />
          </div>

          <div>
            <label className="cf-label" htmlFor="up-soft">
              softId
            </label>
            <input
              id="up-soft"
              type="number"
              className="cf-input"
              value={softId}
              min={0}
              onChange={(e) => setSoftId(Number(e.target.value))}
            />
          </div>

          <div>
            <label className="cf-label" htmlFor="up-only">
              Chỉ fallback cho profile (cách nhau bằng dấu phẩy)
            </label>
            <input
              id="up-only"
              className="cf-input cf-mono"
              value={onlyProfiles}
              onChange={(e) => setOnlyProfiles(e.target.value)}
              placeholder="để trống = cho phép tất cả"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Có giá trị thì captcha <strong>chưa nhận diện được profile</strong> sẽ KHÔNG đi
              upstream — để một site lạ không âm thầm tiêu tiền của bạn.
            </p>
          </div>
        </div>

        {saveError !== null && (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
            {saveError}
          </p>
        )}
        {saved && (
          <p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
            Đã lưu. Có hiệu lực ngay, không cần khởi động lại. Thay đổi đã được ghi vào AuditLog
            (key đã che).
          </p>
        )}

        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          {saving ? 'Đang lưu…' : 'Lưu cấu hình'}
        </button>
      </div>
    </div>
  );
}
